import {
  Annotation,
  AnnotationOverlaps,
  AnnotationType,
} from "./annotationsHelper";

export const ANNOTATION_CHIME_PRIORITY: readonly AnnotationType[] = [
  AnnotationType.Note,
  AnnotationType.Highlight,
  AnnotationType.Bookmark,
];

interface BellPartial {
  /** Absolute frequency in Hz. */
  frequency: number;
  /** Relative gain (0..1) of this partial within the bell. */
  gain: number;
  /** Seconds until this partial decays to near-silence. */
  decay: number;
}

interface BellSpec {
  partials: BellPartial[];
  peakGain: number;
}

const ATTACK_SECONDS = 0.005;
const DECAY_FLOOR = 0.0001;
const DEFAULT_PEAK_GAIN = 0.18;

// Bells are built from a fundamental sine + overtones, each with its own
// envelope. Higher partials decay faster (typical bell behavior). Pitches are
// chosen so the three types are immediately distinguishable.
const BELLS: Record<AnnotationType, BellSpec> = {
  [AnnotationType.Bookmark]: {
    peakGain: DEFAULT_PEAK_GAIN,
    partials: [
      { frequency: 1318.51, gain: 1.0, decay: 1.1 },
      { frequency: 2637.02, gain: 0.45, decay: 0.55 },
      { frequency: 3956.0, gain: 0.18, decay: 0.28 },
    ],
  },
  [AnnotationType.Highlight]: {
    peakGain: DEFAULT_PEAK_GAIN,
    partials: [
      { frequency: 880.0, gain: 1.0, decay: 1.4 },
      { frequency: 1760.0, gain: 0.5, decay: 0.7 },
      { frequency: 2640.0, gain: 0.2, decay: 0.4 },
    ],
  },
  [AnnotationType.Note]: {
    peakGain: DEFAULT_PEAK_GAIN,
    partials: [
      { frequency: 587.33, gain: 1.0, decay: 1.7 },
      { frequency: 1174.66, gain: 0.55, decay: 0.85 },
      { frequency: 1760.0, gain: 0.22, decay: 0.45 },
      { frequency: 3520.0, gain: 0.08, decay: 0.22 },
    ],
  },
};

/**
 * Plays a short bell-like Web Audio tone for each annotation type encountered
 * during TTS. Each bell is an additive sum of sine partials with an
 * exponential decay envelope. `prime()` should be called from a user gesture
 * (e.g. when TTS starts) so the AudioContext is warm by the time the first
 * chime fires.
 */
export default class AnnotationChime {
  private audioContext: AudioContext;
  private disposed = false;
  private lastSignals: Partial<Record<AnnotationType, string | null>> = {};

  constructor() {
    this.audioContext = new AudioContext();
  }

  prime(): void {
    if (this.disposed) return;
    if (this.audioContext?.state === "suspended") {
      this.audioContext?.resume();
    }
  }

  play(type: AnnotationType): void {
    if (this.disposed) return;
    this.strikeBell(this.audioContext, BELLS[type]);
  }

  dispose(): void {
    if (this.disposed) return;
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.disposed = true;
  }

  /**
   * Plays at most one chime for the given overlaps, choosing the
   * highest-priority annotation type whose location changed since the previous
   * dispatch. Each type's `location` is the change-detection signal (for notes,
   * the earliest overlapping note leads), so a chime fires once on entry and
   * won't repeat while the same annotation stays in scope. A null signal means
   * "not present".
   */
  dispatch(overlaps: AnnotationOverlaps): void {
    const signalByType: Record<AnnotationType, Annotation | null> = {
      [AnnotationType.Bookmark]: overlaps[AnnotationType.Bookmark],
      [AnnotationType.Highlight]: overlaps[AnnotationType.Highlight],
      [AnnotationType.Note]: overlaps.notes[0] ?? null,
    };
    const changedTypes: AnnotationType[] = [];
    for (const type of ANNOTATION_CHIME_PRIORITY) {
      const nextSignal = signalByType[type]?.location ?? null;
      const lastSignal = this.lastSignals[type] ?? null;
      if (nextSignal !== lastSignal) {
        this.lastSignals[type] = nextSignal;
        if (nextSignal !== null) {
          changedTypes.push(type);
        }
      }
    }

    const winner = ANNOTATION_CHIME_PRIORITY.find((type) =>
      changedTypes.includes(type),
    );
    if (winner !== undefined) {
      this.play(winner);
    }
  }

  reset(): void {
    this.lastSignals = {};
  }

  private strikeBell(context: AudioContext, spec: BellSpec): void {
    const startTime = context.currentTime;
    for (const partial of spec.partials) {
      const oscillatorNode = context.createOscillator();
      const gainNode = context.createGain();
      oscillatorNode.type = "sine";
      oscillatorNode.frequency.setValueAtTime(partial.frequency, startTime);
      oscillatorNode.connect(gainNode);
      gainNode.connect(context.destination);

      const peak = partial.gain * spec.peakGain;
      const endTime = startTime + partial.decay;
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(peak, startTime + ATTACK_SECONDS);
      gainNode.gain.exponentialRampToValueAtTime(DECAY_FLOOR, endTime);

      oscillatorNode.start(startTime);
      oscillatorNode.stop(endTime);
    }
  }
}
